import { fetchCustomerCatalog } from '../utils/customerRegistryRequests';
import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import { fiscalName } from '../utils/fiscalName';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaAddressBook, FaPlus, FaSyncAlt, FaSearch, FaLink, FaUserTie, FaSave, FaArrowRight, FaFileInvoiceDollar, FaFileExcel, FaCheckSquare } from 'react-icons/fa';
import api from '../api';
import InfoTip from '../components/InfoTip';
import './Customers.css';
import { renderNfseCustomerDescription } from '../utils/nfseDescriptionPresets';

const digitsOnly = value => String(value || '').replace(/\D/g, '');
const maskDocument = value => {
  const digits = digitsOnly(value).slice(0, 14);
  return digits.length > 11
    ? digits.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
    : digits.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
};
const maskCep = value => digitsOnly(value).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
const maskPhone = value => {
  const digits = digitsOnly(value).slice(0, 13);
  if (digits.startsWith('55') && digits.length > 11) return '+55 ' + maskPhone(digits.slice(2));
  if (digits.length <= 2) return digits ? '(' + digits : '';
  const number = digits.slice(2);
  const prefix = '(' + digits.slice(0, 2) + ') ';
  return prefix + (number.length > 4 ? number.slice(0, number.length - 4) + '-' + number.slice(-4) : number);
};
const formatMoney = value => {
  const digits = digitsOnly(value).slice(0, 15);
  return digits ? (Number(digits) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
};
const asMoney = value => {
  if (value == null || value === '') return '';
  if (/^\d+(?:\.\d{1,2})?$/.test(String(value))) {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
};
const savedContacts = (customer, type) => {
  const list = customer[type];
  if (Array.isArray(list) && list.length) return list.map(item => typeof item === 'string'
    ? { value: item, label: '', isDefault: false } : { value: item.value || '', label: item.label || '', isDefault: Boolean(item.isDefault) });
  const single = customer[type === 'phones' ? 'phone' : 'email'];
  return single ? [{ value: single, label: '', isDefault: true }] : [];
};
const emptyCustomer = () => ({
  name: '', tradeName: '', number: '', complement: '', neighborhood: '', taxId: '', email: '', phone: '', phones: [], emails: [], zipCode: '',
  address: '', city: '', state: '', notes: '', status: 'ACTIVE',
  defaultServiceCode: '', defaultServiceDescription: '', defaultAmount: '', defaultTemplateId: '',
  noteDescriptionPresets: [],
});
const SOURCES = [
  { id: 'FINANCE', label: 'Financeiro' },
  { id: 'APPRENTICE', label: 'Jovem Aprendiz' },
  { id: 'CRM', label: 'CRM' },
];
const labels = {
  FINANCE: 'Financeiro', APPRENTICE: 'Jovem Aprendiz', CRM: 'CRM', FORM: 'Formulário',
};
const inputStyle = 'mt-1 min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-400';
const mainButton = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50';
const neutralButton = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50';
const errorMessage = (err) => err?.response?.data?.error || err?.message || 'A operação não pôde ser concluída.';

export default function Customers({ openTab }) {
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const requestSequence = useRef(0);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formBaseline = useRef(null);
  const [form, setForm] = useState(emptyCustomer);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cepStatus, setCepStatus] = useState({ type: 'idle', message: '', address: null });

  const [source, setSource] = useState('FINANCE');
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [importMode, setImportMode] = useState('bulk');
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkOffset, setBulkOffset] = useState(0);
  const [bulkHasMore, setBulkHasMore] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [spreadsheetFile, setSpreadsheetFile] = useState(null);
  const [spreadsheetPreview, setSpreadsheetPreview] = useState(null);
  const [formProcessId, setFormProcessId] = useState('');
  const [formFields, setFormFields] = useState(null);
  const [formMapping, setFormMapping] = useState({ name: '', taxId: '', email: '', phone: '' });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 320);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    api.get('/nfse/templates').then(response => setTemplates(Array.isArray(response.data) ? response.data : []))
      .catch(() => setTemplates([])); // Usuários de Financeiro/CRM podem não ter acesso fiscal.
  }, []);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setBusy(true);
    try {
      const records = await fetchCustomerCatalog(api, {q:debouncedQuery || undefined}, () => sequence === requestSequence.current);
      if (!records) return;
      setCustomers(records);
      setError('');
    } catch (err) {
      if (sequence === requestSequence.current) setError(errorMessage(err));
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }, [debouncedQuery]);

  useEffect(() => { load(); }, [load]);
  useCustomerRegistryRefresh(kind => {
    if (kind !== 'templates') load();
    if (kind !== 'registry') api.get('/nfse/templates').then(response => setTemplates(Array.isArray(response.data) ? response.data : [])).catch(() => {});
  });

  // Consulta de CEP debounced e cancelável; campos já preenchidos pelo usuário
  // não são substituídos automaticamente (existe botão para aplicar novamente).
  useEffect(() => {
    const cep = digitsOnly(form.zipCode);
    if (!editorOpen || cep.length !== 8) {
      setCepStatus({ type: 'idle', message: '', address: null });
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCepStatus({ type: 'loading', message: 'Consultando CEP...', address: null });
      try {
        const response = await fetch('https://viacep.com.br/ws/' + cep + '/json/', { signal: controller.signal });
        if (!response.ok) throw new Error('Não foi possível consultar o CEP.');
        const data = await response.json();
        if (data.erro) throw new Error('CEP não encontrado. Confira os números.');
        const address = { address: data.logradouro || '', neighborhood: data.bairro || '', city: data.localidade || '', state: data.uf || '' };
        setCepStatus({ type: 'success', message: 'Endereço localizado.', address });
        setForm(previous => digitsOnly(previous.zipCode) !== cep ? previous : ({
          ...previous,
          address: previous.address || address.address,
          neighborhood: previous.neighborhood || address.neighborhood,
          city: previous.city || address.city,
          state: previous.state || address.state,
        }));
      } catch (error) {
        if (!controller.signal.aborted) setCepStatus({ type: 'error', message: error.message || 'Consulta indisponível. Preencha manualmente.', address: null });
      }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [editorOpen, form.zipCode]);
  const applyCepAddress = () => {
    if (!cepStatus.address) return;
    setForm(previous => ({ ...previous, ...cepStatus.address }));
  };

  const edited = customers.find(c => c.id === editingId) || null;
  const activeCount = useMemo(() => customers.filter(c => c.status === 'ACTIVE').length, [customers]);
  const visibleCustomers = useMemo(() => customers.filter(c => statusFilter === 'all' || c.status === statusFilter), [customers, statusFilter]);

  const hasFormChanges = () => editorOpen && formBaseline.current !== null
    && JSON.stringify(form) !== formBaseline.current;
  const closeEditor = () => {
    if (hasFormChanges() && !window.confirm('Há alterações não salvas neste cliente. Deseja descartá-las?')) return false;
    formBaseline.current = null;
    setEditorOpen(false);
    setEditingId(null);
    return true;
  };
  useEffect(() => {
    if (!editorOpen) return undefined;
    const warn = event => {
      if (formBaseline.current === null || JSON.stringify(form) === formBaseline.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editorOpen, form]);
  const toggleImport = () => {
    if (editorOpen && !closeEditor()) return;
    setIntegrationOpen(open => !open);
  };


  const openCreate = () => {
    if (editorOpen && !closeEditor()) return;
    const nextForm = emptyCustomer();
    formBaseline.current = JSON.stringify(nextForm);
    setEditingId(null);
    setForm(nextForm);
    setNotice('');
    setError('');
    setEditorOpen(true);
    setShowAdvanced(false);
    setCepStatus({ type: 'idle', message: '', address: null });
  };
  const edit = (customer) => {
    if (editorOpen && !closeEditor()) return;
    const nextForm = { ...emptyCustomer(), ...customer, defaultAmount: asMoney(customer.defaultAmount),
      defaultServiceDescription: (customer.noteDescriptionPresets || []).find(preset => preset.isDefault)?.text || customer.defaultServiceDescription || '',
      phones: savedContacts(customer, 'phones'), emails: savedContacts(customer, 'emails'),
      noteDescriptionPresets: Array.isArray(customer.noteDescriptionPresets) ? customer.noteDescriptionPresets : [] };
    formBaseline.current = JSON.stringify(nextForm);
    setEditingId(customer.id);
    setForm(nextForm);
    setEditorOpen(true);
    setNotice('');
    setError('');
    setShowAdvanced(Boolean(customer.defaultServiceCode || customer.defaultAmount || customer.defaultTemplateId || customer.noteDescriptionPresets?.length));
  };
  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      // Evita envios acidentais duplicados antes da validação de contatos no servidor.
      for (const kind of ['phones', 'emails']) {
        const values = (form[kind] || []).map(item => kind === 'phones' ? digitsOnly(item.value) : (item.value || '').trim().toLowerCase()).filter(Boolean);
        if (new Set(values).size !== values.length) throw new Error(kind === 'phones' ? 'Remova os telefones duplicados.' : 'Remova os e-mails duplicados.');
      }
      if (editingId) {
        const baseline = formBaseline.current ? JSON.parse(formBaseline.current) : {};
        const changes = Object.fromEntries(Object.entries(form).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(baseline[key])));
        await api.put('/customers/' + editingId, changes);
      }
      else await api.post('/customers', form);
      setNotice(editingId ? 'Cadastro atualizado nos módulos integrados.' : 'Cliente cadastrado e integrado.');
      formBaseline.current = null;
      setEditorOpen(false);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const field = name => event => {
    const value = event.target.value;
    setForm(prev => ({ ...prev, [name]: name === 'taxId' ? digitsOnly(value).slice(0, 14)
      : name === 'zipCode' ? digitsOnly(value).slice(0, 8)
      : name === 'state' ? value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)
      : name === 'defaultAmount' ? formatMoney(value) : name === 'name' ? fiscalName(value) : value }));
  };
  const updateContact = (kind, index, changes) => setForm(prev => ({
    ...prev, [kind]: (prev[kind] || []).map((item, i) => i === index ? { ...item, ...changes } : item),
  }));
  const setPrimaryContact = (kind, index) => setForm(prev => ({
    ...prev, [kind]: (prev[kind] || []).map((item, i) => ({ ...item, isDefault: i === index })),
  }));
  const addContact = kind => setForm(prev => ({
    ...prev, [kind]: [...(prev[kind] || []), { value: '', label: '', isDefault: !(prev[kind] || []).length }],
  }));
  const removeContact = (kind, index) => setForm(prev => {
    const contacts = (prev[kind] || []).filter((_, i) => i !== index);
    if (contacts.length && !contacts.some(item => item.isDefault)) contacts[0] = { ...contacts[0], isDefault: true };
    return { ...prev, [kind]: contacts };
  });
  const renderContacts = (kind, title, hint) => {
    const contacts = form[kind] || [];
    const phone = kind === 'phones';
    return <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900">{title}<InfoTip text={hint}/></h3>
        <button type="button" disabled={contacts.length >= 50} onClick={() => addContact(kind)} className={neutralButton}>
          <FaPlus size={11}/> Adicionar {phone ? 'número' : 'e-mail'}
        </button>
      </div>
      {!contacts.length && <p className="mt-3 text-xs text-slate-500">Nenhum contato cadastrado. Adicione quantos precisar (até 50).</p>}
      <div className="mt-3 space-y-2">
        {contacts.map((contact, index) => <div key={index} className="grid items-end gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto]">
          <label className="min-w-0 text-xs font-medium text-slate-700">
            {phone ? 'Telefone / WhatsApp' : 'E-mail'} {index + 1}
            <input className={inputStyle} type={phone ? 'tel' : 'email'} inputMode={phone ? 'tel' : 'email'}
              autoComplete="off" maxLength={phone ? 23 : 200} placeholder={phone ? '(14) 99999-9999' : 'contato@empresa.com.br'}
              value={phone ? maskPhone(contact.value) : contact.value || ''}
              onChange={event => updateContact(kind, index, { value: phone ? digitsOnly(event.target.value).slice(0, 13) : event.target.value })}/>
          </label>
          <label className="min-w-0 text-xs font-medium text-slate-700">Identificação (opcional)
            <input className={inputStyle} maxLength={40} placeholder={phone ? 'Comercial, celular...' : 'Financeiro, pessoal...'}
              value={contact.label || ''} onChange={event => updateContact(kind, index, { label: event.target.value })}/>
          </label>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <button type="button" disabled={!contact.value} aria-pressed={Boolean(contact.isDefault)}
              onClick={() => setPrimaryContact(kind, index)} className={'min-h-[44px] rounded-xl border px-3 text-xs font-semibold ' +
              (contact.isDefault ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-blue-50')}>
              {contact.isDefault ? '✓ Padrão' : 'Definir padrão'}
            </button>
            <button type="button" aria-label={'Remover ' + title.toLowerCase() + ' ' + (index + 1)}
              onClick={() => removeContact(kind, index)} className="min-h-[44px] rounded-xl border border-red-100 px-3 text-xs font-medium text-red-700">Remover</button>
          </div>
        </div>)}
      </div>
    </section>;
  };
  const presets = Array.isArray(form.noteDescriptionPresets) ? form.noteDescriptionPresets : [];
  const updatePreset = (index, updates) => setForm(prev => ({
    ...prev, noteDescriptionPresets: (prev.noteDescriptionPresets || []).map((item, position) =>
      position === index ? { ...item, ...updates } : item),
  }));
  const setMainPreset = (index) => setForm(prev => ({ ...prev,
    noteDescriptionPresets: (prev.noteDescriptionPresets || []).map((item, position) =>
      ({ ...item, isDefault: position === index })),
  }));
  const removePreset = (index) => setForm(prev => ({ ...prev,
    noteDescriptionPresets: (prev.noteDescriptionPresets || []).filter((_, position) => position !== index),
  }));
  const addPreset = () => setForm(prev => ({ ...prev,
    noteDescriptionPresets: [...(prev.noteDescriptionPresets || []), { name: '', text: '', isDefault: !(prev.noteDescriptionPresets || []).length }],
  }));

  const loadSources = async () => {
    setError('');
    setBusy(true);
    try {
      const response = await api.get('/customers/sources/' + source, { params: { q: sourceSearch || undefined } });
      setSourceOptions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(errorMessage(err));
      setSourceOptions([]);
    } finally {
      setBusy(false);
    }
  };
  const importSource = async () => {
    if (!sourceId) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.post('/customers/' + editingId + '/links', { source, sourceId: Number(sourceId) });
        setNotice('Registro vinculado ao cliente.');
      } else {
        await api.post('/customers/import/' + source + '/' + sourceId);
        setNotice('Cadastro importado ou vinculado pelo CPF/CNPJ.');
      }
      setIntegrationOpen(false);
      setSourceId('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const publishToSource = async (customer, sourceType) => {
    const moduleName = labels[sourceType] || sourceType;
    if (!window.confirm('Criar ou vincular este cliente como empresa no ' + moduleName + '?')) return;
    setSaving(true); setError('');
    try {
      const response = await api.post('/customers/' + customer.id + '/publish/' + sourceType);
      setNotice(response.data?.result === 'CREATED'
        ? 'Cadastro criado em ' + moduleName + ' e vinculado ao cliente.'
        : 'Cadastro existente localizado e vinculado em ' + moduleName + '.');
      await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const loadBulkCandidates = async (append = false) => {
    const offset = append ? bulkOffset : 0;
    setBulkBusy(true);
    setError('');
    try {
      const response = await api.get('/customers/import/bulk/' + source, { params: { q: sourceSearch || undefined, offset } });
      const data = response.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      setBulkCandidates(prev => append ? [...prev, ...items] : items);
      setBulkOffset(offset + items.length);
      setBulkHasMore(Boolean(data.hasMore));
      setBulkResults(null);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBulkBusy(false); }
  };

  const toggleBulk = (id) => setBulkSelected(prev =>
    prev.includes(id) ? prev.filter(item => item !== id)
      : prev.length >= 200 ? prev : [...prev, id]);

  const selectVisibleBulk = () => {
    const ids = bulkCandidates.map(item => Number(item.id));
    setBulkSelected(prev => ids.every(id => prev.includes(id))
      ? prev.filter(id => !ids.includes(id))
      : Array.from(new Set([...prev, ...ids])).slice(0, 200));
  };

  const importBulk = async () => {
    if (!bulkSelected.length || !window.confirm('Importar ' + bulkSelected.length + ' registros de ' + labels[source] + '? Cadastros existentes não serão sobrescritos.')) return;
    setSaving(true); setError('');
    try {
      const response = await api.post('/customers/import/bulk/' + source, { ids: bulkSelected });
      setBulkResults(response.data);
      setNotice((response.data?.created || 0) + ' criados, ' + (response.data?.linked || 0) + ' vinculados, ' + (response.data?.alreadyLinked || 0) + ' já vinculados.');
      setBulkSelected([]);
      await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const downloadTemplate = async () => {
    setBulkBusy(true); setError('');
    try {
      const response = await api.get('/customers/import/spreadsheet/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'modelo-importacao-clientes.xlsx';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBulkBusy(false); }
  };

  const sendSpreadsheet = async (action) => {
    if (!spreadsheetFile || (action === 'commit' && !spreadsheetPreview)) return;
    if (action === 'commit' && !window.confirm('Confirmar importação de até ' + spreadsheetPreview.ready + ' novos clientes? A planilha será validada novamente e nenhum cadastro existente será sobrescrito.')) return;
    setBulkBusy(true); setError('');
    try {
      const data = new FormData();
      data.append('file', spreadsheetFile);
      const response = await api.post('/customers/import/spreadsheet/' + action, data);
      if (action === 'preview') {
        setSpreadsheetPreview(response.data);
      } else {
        setBulkResults(response.data);
        setNotice((response.data?.created || 0) + ' clientes criados por planilha. ' + (response.data?.skipped || 0) + ' já existentes.');
        setSpreadsheetPreview(null);
        await load();
      }
    } catch (err) { setError(errorMessage(err)); }
    finally { setBulkBusy(false); }
  };

  const readFormFields = async () => {
    if (!formProcessId) return;
    try {
      const response = await api.get('/customers/from-process/' + formProcessId + '/fields');
      setFormFields(response.data);
      setError('');
    } catch (err) { setError(errorMessage(err)); }
  };
  const importForm = async () => {
    setSaving(true); setError('');
    try {
      await api.post('/customers/from-process/' + formProcessId, { mapping: formMapping });
      setNotice('Cliente associado à solicitação.');
      setFormFields(null); setFormProcessId(''); await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const openGeneralDefaults = async () => {
    try {
      if (typeof openTab !== 'function') throw new Error('Navegação indisponível nesta tela.');
      const { default: CompanyProfile } = await import('./CompanyProfile');
      openTab('company', 'Dados Cadastrais', CompanyProfile, { openTab, initialPanel: 'templates' });
    } catch (err) { setError('Não foi possível abrir os padrões gerais. ' + errorMessage(err)); }
  };

  const editableField = (label, name, options = {}) => <label key={name} className="block min-w-0 text-xs font-medium text-slate-700">
    {label}
    <input value={name === 'taxId' ? maskDocument(form[name]) : name === 'zipCode' ? maskCep(form[name]) : name === 'name' ? fiscalName(form[name]) : form[name] || ''}
      onChange={field(name)} required={options.required} inputMode={options.inputMode}
      autoComplete={options.autoComplete} placeholder={options.placeholder} type={options.type || 'text'} maxLength={options.maxLength}
      className={inputStyle} />
  </label>;

  return <div className="customers-ui mx-auto w-full max-w-7xl min-w-0 space-y-5 pb-10">
    <header className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex min-w-0 items-center gap-3">
        <span className="rounded-xl bg-blue-50 p-3 text-blue-700"><FaAddressBook size={21}/></span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Clientes</h1>
          <p className="text-xs text-slate-500">{customers.length} registros · {activeCount} ativos <InfoTip text="Cadastro central com vínculos ao Financeiro, Jovem Aprendiz, CRM, Formulários e NFS-e."/></p>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={load} disabled={busy} className={neutralButton} aria-label="Atualizar lista"><FaSyncAlt className={busy ? 'animate-spin' : ''}/></button>
        <button type="button" onClick={openCreate} className={mainButton}><FaPlus/> Novo cliente</button>
      </div>
    </header>
    {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</p>}

    <div className="grid gap-3 sm:grid-cols-3">
      {[
        { title: 'Importar / Vincular', icon: FaLink, action: toggleImport, hint: 'Traga cadastros do Financeiro, Jovem Aprendiz ou CRM sem duplicar CPF/CNPJ.' },
        { title: 'Padrões gerais', icon: FaFileInvoiceDollar, action: openGeneralDefaults, hint: 'Abra Dados Cadastrais → Modelos de notas para definir modelos gerais. Valores específicos são salvos na ficha de cada cliente.' },
        { title: 'Importar formulário', icon: FaUserTie, action: () => setFormFields({ pending: true, fields: [] }), hint: 'Mapeie dados de uma instância de processo autorizada para cadastrar o cliente.' },
      ].map(({ title, icon: Icon, action, hint }) => (
        <div key={title} className="relative min-w-0">
          <button type="button" onClick={action} className="flex min-h-[70px] w-full items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 pr-12 text-left text-sm font-semibold text-slate-800 shadow-sm transition hover:border-blue-300">
            <Icon className="shrink-0 text-blue-600"/>{title}
          </button>
          <span className="absolute right-2 top-3 z-10"><InfoTip text={hint}/></span>
        </div>
      ))}
    </div>

    {integrationOpen && <section className="space-y-3 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-center justify-between gap-2"><h2 className="font-semibold text-slate-900">Importar clientes</h2><button type="button" onClick={() => setIntegrationOpen(false)} className={neutralButton}>Fechar</button></div>
      <div className="grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-1" aria-label="Tipo de importação">
        {[
          ['bulk', 'Módulos'],
          ['spreadsheet', 'Planilha'],
          ['single', 'Individual'],
        ].map(([id, title]) => <button key={id} type="button" aria-pressed={importMode === id}
          onClick={() => { setImportMode(id); setBulkResults(null); setError(''); }}
          className={'min-h-[44px] rounded-lg px-2 text-xs font-semibold transition sm:text-sm ' + (importMode === id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white')}>
          {title}
        </button>)}
      </div>
      {importMode === 'single' && <>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs font-semibold text-slate-700">Origem<select className={inputStyle} value={source} onChange={e => { setSource(e.target.value); setSourceOptions([]); setSourceId(''); }}>{SOURCES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">Localizar registro<input className={inputStyle} placeholder="Nome ou CNPJ" value={sourceSearch} onChange={e => setSourceSearch(e.target.value)}/></label>
      </div>
      <button type="button" disabled={busy} onClick={loadSources} className={neutralButton}><FaSearch/> Buscar registros</button>
      {sourceOptions.length > 0 && <div className="grid gap-2 sm:grid-cols-2">
        {sourceOptions.map(item => <button key={item.id} type="button" onClick={() => setSourceId(String(item.id))}
          className={'min-h-[54px] rounded-xl border p-3 text-left text-sm ' + (String(item.id) === sourceId ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50')}>
          <strong className="block break-words text-slate-900">{item.label}</strong><span className="text-xs text-slate-500">{item.taxId || 'Contato sem CPF/CNPJ'} · #{item.id}</span>
        </button>)}
      </div>}
      <button type="button" disabled={!sourceId || saving} onClick={importSource} className={mainButton}>{editingId ? 'Vincular ao cliente selecionado' : 'Importar cliente'}</button>
      </>}
      {importMode === 'bulk' && <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs font-semibold text-slate-700">Importar de
            <select value={source} onChange={event => {
              setSource(event.target.value); setBulkCandidates([]); setBulkSelected([]);
              setBulkOffset(0); setBulkHasMore(false); setBulkResults(null);
            }} className={inputStyle}>
              {SOURCES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-700 sm:col-span-2">Pesquisar na origem
            <input value={sourceSearch} onChange={event => setSourceSearch(event.target.value)}
              placeholder="Nome, telefone ou CNPJ" className={inputStyle} />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={neutralButton} disabled={bulkBusy} onClick={() => loadBulkCandidates(false)}>
            <FaSearch/> {bulkBusy ? 'Buscando...' : 'Buscar'}
          </button>
          {bulkCandidates.length > 0 && <button type="button" onClick={selectVisibleBulk} className={neutralButton}><FaCheckSquare/> Selecionar exibidos</button>}
          <span className="text-xs text-slate-500">{bulkSelected.length}/200 selecionados <InfoTip text="Selecione vários registros, inclusive entre páginas. O sistema cria ou vincula cada cliente sem sobrescrever cadastros existentes." /></span>
        </div>
        {bulkCandidates.length > 0 && <div className="grid max-h-[400px] gap-2 overflow-y-auto rounded-xl border border-slate-100 p-2 sm:grid-cols-2">
          {bulkCandidates.map(item => <label key={item.id} className={'flex min-h-[60px] cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm ' + (bulkSelected.includes(Number(item.id)) ? 'border-blue-400 bg-blue-50' : 'border-slate-200')}>
            <input type="checkbox" checked={bulkSelected.includes(Number(item.id))}
              onChange={() => toggleBulk(Number(item.id))}
              className="h-5 w-5 shrink-0 accent-blue-600" />
            <span className="min-w-0"><strong className="block break-words text-slate-900">{item.label}</strong><span className="text-xs text-slate-500">{item.taxId || 'Sem documento'} · #{item.id}</span></span>
          </label>)}
        </div>}
        {bulkHasMore && <button type="button" disabled={bulkBusy} onClick={() => loadBulkCandidates(true)} className={neutralButton}>Carregar mais</button>}
        <button type="button" disabled={saving || bulkBusy || bulkSelected.length === 0}
          onClick={importBulk} className={mainButton}>Importar {bulkSelected.length} selecionados</button>
      </div>}
      {importMode === 'spreadsheet' && <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={bulkBusy} onClick={downloadTemplate} className={neutralButton}><FaFileExcel/> Baixar modelo XLSX</button>
          <InfoTip text="Modelo com colunas padronizadas, CPF/CNPJ como texto, situação e instruções. Máximo de 500 clientes por arquivo, até 2 MB."/>
        </div>
        <label className="block rounded-xl border border-dashed border-blue-200 bg-blue-50/50 p-4 text-sm font-semibold text-slate-700">
          Selecionar planilha (.xlsx)
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="mt-2 block w-full text-sm" onChange={event => {
              setSpreadsheetFile(event.target.files?.[0] || null);
              setSpreadsheetPreview(null); setBulkResults(null);
            }}/>
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!spreadsheetFile || bulkBusy} onClick={() => sendSpreadsheet('preview')} className={neutralButton}>
            {bulkBusy ? 'Analisando...' : 'Conferir planilha'}
          </button>
          <button type="button" disabled={!spreadsheetPreview || !spreadsheetPreview.ready || bulkBusy}
            onClick={() => sendSpreadsheet('commit')} className={mainButton}>Confirmar importação</button>
        </div>
        {spreadsheetPreview && <div className="rounded-xl border border-slate-200 p-3 text-sm">
          <p className="font-semibold text-slate-800">Prévia: {spreadsheetPreview.total} linhas · {spreadsheetPreview.ready} prontas · {spreadsheetPreview.duplicates} já existentes · {spreadsheetPreview.invalid} inválidas</p>
          <div className="mt-2 max-h-[260px] space-y-1 overflow-y-auto">
            {(spreadsheetPreview.rows || []).slice(0, 120).map(row => <div key={row.line} className="flex flex-wrap gap-2 border-b border-slate-100 py-1 text-xs">
              <span className="w-12 shrink-0 text-slate-500">#{row.line}</span>
              <span className="min-w-0 flex-1 break-words text-slate-800">{fiscalName(row.name) || 'Sem nome'}</span>
              <span className={row.error ? 'font-semibold text-red-700' : row.existing ? 'text-amber-700' : 'text-emerald-700'}>
                {row.error || (row.existing ? 'Já existe' : 'Pronto')}
              </span>
            </div>)}
          </div>
        </div>}
      </div>}
      {bulkResults && <div role="status" className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-slate-700">
        <strong className="block text-blue-900">Importação concluída</strong>
        <p className="mt-1">{bulkResults.created || 0} criados · {bulkResults.linked || 0} vinculados · {bulkResults.alreadyLinked || bulkResults.skipped || 0} já existentes · {bulkResults.failed || 0} falhas</p>
        {(bulkResults.details || []).slice(0, 25).map((item, index) => <p key={index} className="mt-1 text-xs text-red-700">Linha/registro {item.line || item.id}: {item.error}</p>)}
      </div>}
    </section>}

    {formFields && <section className="space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-center justify-between"><h2 className="font-semibold text-slate-900">Cliente a partir de formulário</h2><button type="button" onClick={() => setFormFields(null)} className={neutralButton}>Fechar</button></div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input className={inputStyle} placeholder="Número da solicitação" value={formProcessId} onChange={e => setFormProcessId(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
        <button type="button" disabled={!formProcessId} onClick={readFormFields} className={neutralButton}>Localizar campos</button>
      </div>
      {formFields.fields.length > 0 && <>
        <div className="grid gap-3 sm:grid-cols-2">
          {[['name', 'Nome ou razão social'], ['taxId', 'CPF/CNPJ'], ['email', 'E-mail'], ['phone', 'Telefone']].map(([key, label]) => (
            <label key={key} className="text-xs font-semibold text-slate-700">{label}<select className={inputStyle} value={formMapping[key] || ''} onChange={e => setFormMapping(prev => ({ ...prev, [key]: e.target.value }))}>
              <option value="">Selecionar campo</option>{formFields.fields.map(name => <option key={name} value={name}>{name}</option>)}
            </select></label>
          ))}
        </div>
        <button disabled={!formMapping.name || saving} type="button" onClick={importForm} className={mainButton}>Importar dados do formulário</button>
      </>}
    </section>}

    {editorOpen && <section className="space-y-4 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{editingId ? 'Editar cliente' : 'Novo cliente'}</h2>
        <button type="button" className={neutralButton} onClick={closeEditor}>Fechar</button>
      </div>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {editableField('Nome / razão social *', 'name', { required: true, maxLength: 200 })}
          {editableField('Nome fantasia', 'tradeName', { maxLength: 200 })}
          {editableField('CPF / CNPJ', 'taxId', { maxLength: 18, inputMode: 'numeric', placeholder: '000.000.000-00 ou 00.000.000/0000-00' })}
          <label className="min-w-0 text-xs font-medium text-slate-700">Status
            <select className={inputStyle} value={form.status} onChange={field('status')}>
              <option value="ACTIVE">Ativo</option><option value="INACTIVE">Inativo</option>
            </select>
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {renderContacts('phones', 'Telefones e WhatsApp', 'Cadastre vários números, identifique cada um e marque o contato principal. O principal será usado nas integrações que aceitam um único número.')}
          {renderContacts('emails', 'Endereços de e-mail', 'Cadastre vários e-mails e marque o principal para a NFS-e e outras integrações. Os demais permanecem salvos na ficha.')}
        </div>
        <section className="space-y-3 rounded-2xl border border-blue-100 bg-blue-50/40 p-3 sm:p-4">
          <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900">Endereço <InfoTip text="Digite o CEP para buscar rua, bairro, cidade e UF. Endereços digitados manualmente não serão sobrescritos sem sua ação."/></h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {editableField('CEP', 'zipCode', { maxLength: 9, inputMode: 'numeric', autoComplete: 'postal-code', placeholder: '00000-000' })}
            {editableField('Cidade', 'city', { maxLength: 120, autoComplete: 'address-level2' })}
            {editableField('UF', 'state', { maxLength: 2, autoComplete: 'address-level1', placeholder: 'SP' })}
            <div className="sm:col-span-3">{editableField('Rua / avenida', 'address', { maxLength: 255, autoComplete: 'street-address', placeholder: 'Rua ou avenida' })}</div>
            {editableField('Número', 'number', { maxLength: 30 })}
            {editableField('Complemento', 'complement', { maxLength: 120 })}
            {editableField('Bairro', 'neighborhood', { maxLength: 120 })}
          </div>
          {cepStatus.type !== 'idle' && <div role="status" aria-live="polite" className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className={cepStatus.type === 'error' ? 'text-amber-700' : cepStatus.type === 'success' ? 'text-emerald-700' : ''}>{cepStatus.message}</span>
            {cepStatus.address && <button type="button" className="rounded-lg border border-blue-200 bg-white px-3 py-2 font-semibold text-blue-700" onClick={applyCepAddress}>Aplicar endereço encontrado</button>}
          </div>}
        </section>
        <label className="block rounded-xl border border-blue-100 bg-blue-50/40 p-4 text-sm font-semibold text-slate-800">Descrição padrão das notas deste cliente
          <textarea rows={3} maxLength={4000} value={form.defaultServiceDescription || ''}
            onChange={event => { const text = event.target.value; setForm(previous => ({ ...previous, defaultServiceDescription: text, noteDescriptionPresets: (previous.noteDescriptionPresets || []).map(preset => preset.isDefault ? { ...preset, text } : preset) })); }}
            placeholder="Descreva o serviço normalmente prestado a este cliente" className={inputStyle + ' resize-y'}/>
          <span className="mt-1 block text-xs font-normal text-slate-500">Preenchida automaticamente ao selecionar o cliente na nota. Variáveis: {'{{cliente}}, {{mes}}, {{ano}} e {{competencia}}.'}</span>
        </label>
        <button type="button" aria-expanded={showAdvanced} className="flex w-full items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-left text-sm font-semibold text-blue-900" onClick={() => setShowAdvanced(v => !v)}>
          Padrões de serviços deste cliente <span>{showAdvanced ? '−' : '+'}</span>
        </button>
        {showAdvanced && <div className="grid gap-3 rounded-xl bg-slate-50 p-3 sm:grid-cols-2">
          {templates.length > 0 && <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
            Modelo geral de serviço
            <select className={inputStyle} value={form.defaultTemplateId || ''} onChange={event => setForm(prev => ({ ...prev, defaultTemplateId: event.target.value ? Number(event.target.value) : null }))}>
              <option value="">Sem modelo padrão</option>
              {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>}
          {editableField('Código padrão do serviço', 'defaultServiceCode', { maxLength: 20 })}
          {editableField('Valor padrão (R$)', 'defaultAmount', { placeholder: 'Ex.: 250,00' })}
          <div className="min-w-0 space-y-3 sm:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-1 text-sm font-semibold text-slate-900">Descrições de notas
                <InfoTip text="Salve até 10 descrições por cliente. Marque uma como principal. Variáveis aceitas: {{cliente}}, {{mes}}, {{ano}}, {{competencia}}. O texto permanece editável em cada rascunho." />
              </h3>
              <div className="flex flex-wrap gap-2">
                {form.defaultServiceDescription && !presets.some(preset => preset.text === form.defaultServiceDescription) &&
                  <button type="button" disabled={presets.length >= 10} onClick={() => setForm(prev => ({
                    ...prev, noteDescriptionPresets: [...(prev.noteDescriptionPresets || []), {
                      name: 'Descrição ' + ((prev.noteDescriptionPresets || []).length + 1),
                      text: prev.defaultServiceDescription,
                      isDefault: !(prev.noteDescriptionPresets || []).length,
                    }],
                  }))} className={neutralButton}>Adicionar padrão à lista</button>}
                <button type="button" onClick={addPreset} disabled={presets.length >= 10} className={neutralButton}>
                  <FaPlus size={12}/> Nova descrição
                </button>
              </div>
            </div>
            {presets.length === 0 && <p className="text-xs text-slate-500">Salve a descrição usada com frequência para reutilizá-la nas próximas notas.</p>}
            {presets.map((preset, index) => <div key={index} className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <label className="min-w-[160px] flex-1 text-xs font-semibold text-slate-700">
                  Nome da descrição
                  <input value={preset.name || ''} onChange={event => updatePreset(index, { name: event.target.value })}
                    maxLength={80} placeholder="Ex.: Mensalidade de suporte" className={inputStyle}/>
                </label>
                <button type="button" aria-pressed={Boolean(preset.isDefault)} onClick={() => setMainPreset(index)}
                  className={'min-h-[44px] rounded-xl border px-3 text-xs font-semibold ' +
                    (preset.isDefault ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600')}>
                  {preset.isDefault ? 'Principal' : 'Tornar principal'}
                </button>
                <button type="button" onClick={() => removePreset(index)}
                  aria-label={'Excluir descrição ' + (preset.name || index + 1)}
                  className="min-h-[44px] rounded-xl border border-red-100 px-3 text-xs font-semibold text-red-700">Excluir</button>
              </div>
              <label className="block text-xs font-semibold text-slate-700">
                Texto para a NFS-e
                <textarea rows={4} maxLength={4000} value={preset.text || ''}
                  placeholder="Prestação de serviços de suporte técnico referente a {{competencia}}."
                  onChange={event => updatePreset(index, { text: event.target.value })}
                  className={inputStyle + ' resize-y leading-relaxed'}/>
                <span className="mt-1 block text-right text-[11px] font-normal text-slate-500">{(preset.text || '').length}/4000</span>
                {preset.text && <span className="mt-2 block rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs font-normal leading-relaxed text-slate-600">
                  <strong className="block text-slate-700">Prévia:</strong>
                  {renderNfseCustomerDescription(preset.text, { customerName: form.name, competenceDate: new Date().toISOString().slice(0, 10) })}
                </span>}
              </label>
            </div>)}
          </div>
          <InfoTip text="O código e valor próprios do cliente têm prioridade sobre o modelo geral. Se houver descrição principal, ela será sugerida ao selecionar o cliente no Gerenciador NFS." />
        </div>}
        <label className="block text-xs font-semibold text-slate-700">Observações<textarea className={inputStyle} rows={2} maxLength={2000} value={form.notes || ''} onChange={field('notes')} /></label>
        <button disabled={saving} type="submit" className={mainButton}><FaSave/> {saving ? 'Salvando...' : 'Salvar cliente'}</button>
      </form>
      {edited && <div className="space-y-2 border-t border-slate-100 pt-4">
        <h3 className="text-sm font-semibold text-slate-800">Integrações vinculadas</h3>
        {edited.links.map(link => <div key={link.source + ':' + link.sourceId} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 p-3 text-xs">
          <span className="mr-auto font-semibold text-slate-700">{labels[link.source] || link.source} #{link.sourceId}</span>
          {(link.source === 'FINANCE' || link.source === 'APPRENTICE') && <span className="rounded-full bg-emerald-50 px-3 py-2 text-emerald-700">Atualização automática ao salvar</span>}
        </div>)}
        <div className="flex flex-wrap gap-2">
          {['FINANCE', 'APPRENTICE'].filter(sourceType => !(edited.links || []).some(link => link.source === sourceType)).map(sourceType =>
            <button key={sourceType} type="button" disabled={saving || !edited.taxId || edited.taxId.length !== 14}
              onClick={() => publishToSource(edited, sourceType)} className={neutralButton}>
              <FaPlus size={12}/> Criar em {labels[sourceType]}
            </button>)}
        </div>
        <button type="button" onClick={() => { setIntegrationOpen(true); setSourceId(''); }} className={neutralButton}><FaLink/> Adicionar vínculo</button>
      </div>}
    </section>}

    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-semibold text-slate-900">Cadastros <span className="text-xs font-normal text-slate-500">({visibleCustomers.length})</span></h2>
        <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <select aria-label="Filtrar clientes pelo status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} className={inputStyle + ' sm:w-36'}>
            <option value="all">Todos</option><option value="ACTIVE">Ativos</option><option value="INACTIVE">Inativos</option>
          </select>
          <div className="relative w-full sm:w-72">
          <FaSearch aria-hidden="true" className="absolute left-3 top-4 text-xs text-slate-400"/>
          <input aria-label="Buscar clientes" className={inputStyle + ' pl-9'} placeholder="Nome, CPF/CNPJ, e-mail ou telefone" value={query} onChange={e => setQuery(e.target.value)}/>
          </div>
        </div>
      </div>
      {busy ? <p className="py-6 text-center text-sm text-slate-500">Carregando…</p> : visibleCustomers.length === 0
        ? <div className="py-10 text-center"><FaAddressBook className="mx-auto text-3xl text-slate-300"/><p className="mt-3 text-sm font-semibold text-slate-700">Nenhum cliente encontrado</p><button type="button" onClick={openCreate} className={neutralButton + ' mt-3'}>Cadastrar o primeiro cliente</button></div>
        : <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{visibleCustomers.map(customer => <article key={customer.id} className="flex min-w-0 flex-col justify-between gap-4 rounded-xl border border-slate-200 p-4 transition hover:border-blue-200 hover:shadow-sm">
            <div>
              <div className="flex items-start justify-between gap-2"><h3 className="break-words text-sm font-semibold text-slate-900">{fiscalName(customer.name)}</h3><span className={'rounded-full px-2 py-1 text-[10px] font-semibold ' + (customer.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{customer.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}</span></div>
              <p className="mt-1 text-xs text-slate-500">{customer.taxId ? maskDocument(customer.taxId) : 'Sem CPF/CNPJ'}</p>
              {customer.email && <p className="mt-2 break-all text-xs text-slate-600">{customer.email} <span className="text-blue-600">{(customer.emails?.length || 0) > 1 ? ' (+' + (customer.emails.length - 1) + ')' : ''}</span></p>}
              {customer.phone && <p className="mt-1 text-xs text-slate-600">{maskPhone(customer.phone)} <span className="text-blue-600">{(customer.phones?.length || 0) > 1 ? ' (+' + (customer.phones.length - 1) + ')' : ''}</span></p>}
              <div className="mt-2 flex flex-wrap gap-1">{(customer.links || []).map(link => <span key={link.source + ':' + link.sourceId} className="rounded-md bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-700">{labels[link.source] || link.source}</span>)}</div>
            </div>
            <button type="button" onClick={() => edit(customer)} className={neutralButton + ' w-full'}>Abrir ficha <FaArrowRight className="ml-auto"/></button>
          </article>)}</div>}
    </section>
  </div>;
}
